import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Modal from "../common/Modal";
import Button from "../common/Button";
import Spinner from "../common/Spinner";
import {
  getEmailDraft,
  saveEmailDraft,
  previewEmail,
  sendTestEmail,
  approveEmail,
  continueSending,
  dismissEmail,
  getEmailSettings,
  rewriteEmail,
} from "../../api/email";
import { formatEasternDate } from "../../utils/dates";
import { fillName } from "../../utils/emailText";

// Read, tweak and send one drafted email. Nothing goes out until "Approve &
// send"; closing keeps your edits on the draft for later.

const TIPS = {
  event_thanks:
    "A one-time thank-you to every guest who signed a waiver, so each of them can leave a review. Later follow-ups only go to people who ticked “email me news”.",
  booker_thanks: "Just to the person who booked. Add a personal line about their event if you like.",
  refer: "If you offer a referral thank-you (say, $100 off their next booking), mention it here.",
  rebook: "Only drafted if they haven't booked again since.",
  nudge_lead:
    "Sent from your own email, like your quotes, so their reply comes straight to you. Sending it starts the clock on the no-reply emails, if they're on.",
  newsletter: "Goes to everyone on your list. Check the open dates are still open before you send it.",
  campaign:
    "A follow-up to a lead who hasn't replied, sent from your own email with an unsubscribe link. Dismiss skips this one; the next is drafted on schedule. If they've replied, mark it in their conversation instead.",
  nudge_quote: "Sent from your own email, like your quotes, so their reply comes straight to you.",
  nudge_expiring: "Sent from your own email, like your quotes, so their reply comes straight to you.",
  nudge_balance: "Sent from your own email, like your quotes, so their reply comes straight to you.",
};

const FIELDS = ["subject", "body", "cta_label", "cta_url"];

export default function EmailDraftModal({ draftId, onClose, note = "" }) {
  const [draft, setDraft] = useState(null);
  const [form, setForm] = useState(null);
  const [excluded, setExcluded] = useState([]);
  const [settings, setSettings] = useState(null);
  const [tab, setTab] = useState("edit");
  const [previewHtml, setPreviewHtml] = useState("");
  const [showPeople, setShowPeople] = useState(false);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState(note);
  const [progress, setProgress] = useState(null); // sending state
  const [ask, setAsk] = useState("");
  const changed = useRef(false);
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  useEffect(() => {
    let off = false;
    Promise.all([getEmailDraft(draftId), getEmailSettings().catch(() => null)])
      .then(([d, s]) => {
        if (off) return;
        setDraft(d);
        setForm(Object.fromEntries(FIELDS.map((k) => [k, d[k] || ""])));
        setExcluded(d.excluded_ids || []);
        setSettings(s);
        if (d.status !== "draft") setProgress(d);
      })
      .catch((e) => !off && setError(e.message || "Could not load this email."));
    return () => {
      off = true;
    };
  }, [draftId]);

  const people = useMemo(() => draft?.people || [], [draft]);
  const count = people.filter((p) => !excluded.includes(p.contact_id)).length;
  const sample = people.find((p) => !excluded.includes(p.contact_id) && p.first_name)?.first_name || "";
  const dirty =
    draft &&
    form &&
    (FIELDS.some((k) => (form[k] || "") !== (draft[k] || "")) ||
      excluded.join(",") !== (draft.excluded_ids || []).join(","));

  // Preview renders on the server so it's exactly what people get.
  useEffect(() => {
    if (tab !== "preview" || !form || !draft) return undefined;
    const t = setTimeout(() => {
      previewEmail({ ...form, first_name: sample, audience: draft.audience, kind: draft.kind })
        .then((r) => alive.current && setPreviewHtml(r.html))
        .catch(() => alive.current && setPreviewHtml("<p style='font-family:sans-serif'>Preview unavailable.</p>"));
    }, 250);
    return () => clearTimeout(t);
  }, [tab, form, draft, sample]);

  const close = useCallback(async () => {
    if (dirty && draft?.status === "draft") {
      try {
        await saveEmailDraft(draft.id, { ...form, excluded_ids: excluded });
        changed.current = true;
      } catch {
        /* the old version stays; nothing is lost that was sent */
      }
    }
    onClose(changed.current);
  }, [dirty, draft, form, excluded, onClose]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggle = (id) => setExcluded((xs) => (xs.includes(id) ? xs.filter((x) => x !== id) : [...xs, id]));

  async function test() {
    setBusy("test");
    setError("");
    setNotice("");
    try {
      const r = await sendTestEmail(draft.id, form);
      setNotice(`Test sent to ${r.to}.`);
    } catch (e) {
      setError(e.message || "Couldn't send the test.");
    } finally {
      setBusy("");
    }
  }

  // Claude rewrites what's on screen (including unsaved edits) and saves it.
  async function rewrite(e) {
    e?.preventDefault();
    if (!ask.trim()) return;
    setBusy("rewrite");
    setError("");
    setNotice("");
    try {
      const d = await rewriteEmail(draft.id, ask.trim(), { subject: form.subject, body: form.body });
      setDraft(d);
      setForm(Object.fromEntries(FIELDS.map((k) => [k, d[k] || ""])));
      setAsk("");
      changed.current = true;
    } catch (err) {
      setError(err.message || "Claude couldn't rewrite it.");
    } finally {
      setBusy("");
    }
  }

  async function dismiss() {
    const ask =
      draft.kind === "campaign"
        ? "Skip this follow-up? It won't be sent, and the next one is drafted on schedule."
        : "Dismiss this email? It won't be sent or drafted again.";
    if (!window.confirm(ask)) return;
    setBusy("dismiss");
    try {
      await dismissEmail(draft.id);
      changed.current = true;
      onClose(true);
    } catch (e) {
      setError(e.message || "Couldn't dismiss it.");
      setBusy("");
    }
  }

  async function approve() {
    const who = single && people[0] ? people[0].email : `${count} ${count === 1 ? "person" : "people"}`;
    if (!window.confirm(`Send “${form.subject || "(no subject)"}” to ${who} now?`)) return;
    setBusy("send");
    setError("");
    setNotice("");
    try {
      let res = await approveEmail(draft.id, { ...form, excluded_ids: excluded });
      changed.current = true;
      setProgress(res);
      while (alive.current && res.remaining > 0 && !res.error && !res.capped) {
        res = await continueSending(draft.id);
        if (alive.current) setProgress(res);
      }
    } catch (e) {
      setError(e.message || "Couldn't send it.");
    } finally {
      if (alive.current) setBusy("");
    }
  }

  async function resume() {
    setBusy("send");
    setError("");
    try {
      let res = await continueSending(draft.id);
      setProgress(res);
      while (alive.current && res.remaining > 0 && !res.error && !res.capped) {
        res = await continueSending(draft.id);
        if (alive.current) setProgress(res);
      }
      changed.current = true;
    } catch (e) {
      setError(e.message || "Couldn't continue sending.");
    } finally {
      if (alive.current) setBusy("");
    }
  }

  // One person, sent straight away: personal notes and a lead's follow-ups.
  const single = Boolean(draft && (draft.personal || draft.kind === "campaign"));
  // The postal address is a list-email requirement; a personal note doesn't need it.
  const noAddress = !draft?.personal && settings && !settings.settings?.mailing_address?.trim();
  const context = draft
    ? [draft.customer_name, draft.quote_number, draft.event_date && formatEasternDate(draft.event_date)]
        .filter(Boolean)
        .join(" · ")
    : "";

  const footer =
    !draft || !form ? null : progress ? (
      <div className="flex justify-end gap-2">
        {progress.remaining > 0 && busy !== "send" && <Button onClick={resume}>Keep sending</Button>}
        <Button variant="secondary" onClick={() => onClose(true)} disabled={busy === "send"}>
          {busy === "send" ? "Sending…" : "Done"}
        </Button>
      </div>
    ) : (
      <div className="flex flex-col sm:flex-row sm:items-center gap-2">
        <div className="flex items-center justify-between gap-2 sm:flex-1">
          <Button variant="ghost" size="sm" onClick={dismiss} disabled={Boolean(busy)} className="text-red-700">
            Dismiss
          </Button>
          <Button variant="secondary" size="sm" onClick={test} disabled={Boolean(busy)}>
            {busy === "test" ? "Sending…" : "Send me a test"}
          </Button>
        </div>
        <Button onClick={approve} disabled={Boolean(busy) || count === 0 || noAddress} className="w-full sm:w-auto py-3 sm:py-2">
          {busy === "send"
            ? "Sending…"
            : single && people[0]
              ? `Send to ${people[0].first_name || people[0].email}`
              : `Approve & send to ${count}`}
        </Button>
      </div>
    );

  return (
    <Modal isOpen title={draft ? draft.kind_label : "Email"} onClose={close} footer={footer} maxWidthClass="max-w-2xl">
      {error && <div className="mb-3 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>}
      {notice && (
        <div className="mb-3 rounded-xl border border-birdie-green/25 bg-birdie-green/10 p-3 text-sm text-birdie-green">
          {notice}
        </div>
      )}
      {!draft || !form ? (
        !error && (
          <div className="py-12 flex justify-center">
            <Spinner label="Loading..." />
          </div>
        )
      ) : progress ? (
        <SendProgress p={progress} subject={form.subject || draft.subject} />
      ) : (
        <div className="space-y-4">
          {context && <div className="text-xs text-birdie-charcoal/55 -mt-1">{context}</div>}
          {noAddress && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
              Add your mailing address on the{" "}
              <Link to="/admin/email" className="font-semibold underline">
                Email page
              </Link>{" "}
              before sending. The law requires it at the bottom of these emails.
            </div>
          )}

          <div className="rounded-xl bg-birdie-cream/70 p-3">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="text-sm font-semibold text-birdie-charcoal">
                  To {count} {count === 1 ? "person" : "people"}
                </div>
                <div className="text-xs text-birdie-charcoal/60">{draft.audience_label}</div>
                <Skipped s={draft.skipped} />
              </div>
              {people.length > 1 && (
                <button
                  type="button"
                  onClick={() => setShowPeople((v) => !v)}
                  className="shrink-0 text-xs font-semibold text-birdie-green"
                >
                  {showPeople ? "Hide" : "Choose"}
                </button>
              )}
            </div>
            {showPeople && (
              <ul className="mt-2 max-h-56 overflow-y-auto divide-y divide-birdie-green/10 bg-white rounded-lg border border-birdie-green/10">
                {people.map((p) => (
                  <li key={p.contact_id}>
                    <label className="flex items-center gap-3 px-3 py-2 text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        checked={!excluded.includes(p.contact_id)}
                        onChange={() => toggle(p.contact_id)}
                        className="h-4 w-4 min-h-0 min-w-0 accent-birdie-green"
                      />
                      <span className="min-w-0">
                        <span className="block truncate text-birdie-charcoal">
                          {[p.first_name, p.last_name].filter(Boolean).join(" ") || p.email}
                        </span>
                        <span className="block truncate text-[11px] text-birdie-charcoal/50">{p.email}</span>
                      </span>
                    </label>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {draft.ai_drafted && (
            <div className="rounded-xl border border-violet-200 bg-violet-50 px-3 py-2 text-xs text-violet-900">
              <strong>Written by Claude.</strong> Read it through before you send: check any dates, times and prices.
            </div>
          )}
          {TIPS[draft.kind] && <p className="text-xs text-birdie-charcoal/55">{TIPS[draft.kind]}</p>}

          <div className="flex gap-1 rounded-xl bg-birdie-cream p-1 w-fit">
            {["edit", "preview"].map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setTab(t)}
                className={`px-4 py-1.5 rounded-lg text-sm font-semibold min-h-0 ${
                  tab === t ? "bg-white shadow-sm text-birdie-charcoal" : "text-birdie-charcoal/55"
                }`}
              >
                {t === "edit" ? "Edit" : "Preview"}
              </button>
            ))}
          </div>

          {tab === "edit" ? (
            <div className="space-y-3">
              <Field label="Subject">
                <input value={form.subject} onChange={set("subject")} className={INPUT} maxLength={300} />
              </Field>
              <Field
                label="Message"
                hint="{first_name} becomes each person's first name. [button] is where the button goes."
              >
                <textarea value={form.body} onChange={set("body")} rows={14} className={`${INPUT} leading-relaxed`} />
              </Field>
              {draft.ai_available && (
                <form onSubmit={rewrite} className="rounded-xl bg-birdie-cream/70 p-2.5">
                  <label htmlFor="ask-claude" className="block text-xs font-semibold text-birdie-charcoal/70 mb-1">
                    Ask Claude to change it
                  </label>
                  <div className="flex gap-2">
                    <input
                      id="ask-claude"
                      value={ask}
                      onChange={(e) => setAsk(e.target.value)}
                      placeholder="e.g. shorter, or mention we're booked that day"
                      className={`${INPUT} min-w-0 flex-1`}
                      maxLength={500}
                    />
                    <Button type="submit" size="sm" variant="secondary" disabled={Boolean(busy) || !ask.trim()}>
                      {busy === "rewrite" ? "Writing…" : "Rewrite"}
                    </Button>
                  </div>
                </form>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Button text" hint="Leave both empty for no button.">
                  <input value={form.cta_label} onChange={set("cta_label")} className={INPUT} maxLength={60} />
                </Field>
                <Field label="Button link">
                  <input value={form.cta_url} onChange={set("cta_url")} className={INPUT} inputMode="url" />
                </Field>
              </div>
            </div>
          ) : (
            <div>
              <div className="text-sm text-birdie-charcoal mb-2">
                <span className="text-birdie-charcoal/50">Subject: </span>
                <strong>{fillName(form.subject, sample) || "(no subject)"}</strong>
              </div>
              {previewHtml ? (
                <iframe
                  title="Email preview"
                  srcDoc={previewHtml}
                  sandbox=""
                  className="w-full h-[460px] rounded-xl border border-birdie-green/15 bg-birdie-cream"
                />
              ) : (
                <div className="py-12 flex justify-center">
                  <Spinner label="Rendering..." />
                </div>
              )}
              {sample && <p className="text-[11px] text-birdie-charcoal/45 mt-1">Shown as {sample} will see it.</p>}
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}

const INPUT =
  "w-full rounded-xl border border-birdie-green/20 bg-white px-3 py-2 text-sm text-birdie-charcoal focus:outline-none focus:ring-2 focus:ring-birdie-mustard/40";

function Field({ label, hint, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold text-birdie-charcoal/70 mb-1">{label}</span>
      {children}
      {hint && <span className="block text-[11px] text-birdie-charcoal/45 mt-1">{hint}</span>}
    </label>
  );
}

function Skipped({ s }) {
  if (!s) return null;
  const bits = [
    s.unsubscribed && `${s.unsubscribed} unsubscribed`,
    s.recent && `${s.recent} emailed in the last week`,
    s.bounced && `${s.bounced} bad address${s.bounced === 1 ? "" : "es"}`,
  ].filter(Boolean);
  if (!bits.length) return null;
  return <div className="text-[11px] text-birdie-charcoal/45 mt-0.5">Left out: {bits.join(", ")}</div>;
}

function SendProgress({ p, subject }) {
  const total = Number(p.recipient_count || 0);
  const sent = Number(p.sent || 0);
  const pct = total ? Math.round((sent / total) * 100) : 0;
  const done = p.status === "sent";
  const problem = p.error || p.last_error;
  return (
    <div className="py-4 space-y-3">
      <div className="text-sm text-birdie-charcoal/70">“{subject}”</div>
      <div className="h-2.5 rounded-full bg-birdie-cream overflow-hidden">
        <div className="h-full bg-birdie-green transition-all" style={{ width: `${pct}%` }} />
      </div>
      <div className="text-base font-semibold text-birdie-charcoal">
        {done ? `Sent to ${sent} ${sent === 1 ? "person" : "people"} ✓` : `Sending… ${sent} of ${total}`}
      </div>
      {Number(p.failed) > 0 && (
        <div className="text-sm text-amber-700">{p.failed} couldn't be delivered (bad address).</div>
      )}
      {!done && (problem || p.capped) && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          {problem || "Today's sending limit is reached."} The rest go out automatically, so you can close this.
        </div>
      )}
      {!done && !problem && !p.capped && (
        <p className="text-xs text-birdie-charcoal/50">
          You can close this. Anything left keeps going out within the hour.
        </p>
      )}
    </div>
  );
}
